import React, { useRef, useState } from "react";
import { subirArchivo, abrirArchivoSeguro, listaArchivos, unirArchivos, LARGO_MAXIMO_ARCHIVOS } from "../utils/upload";
import { Ayuda } from "../context/AyudaContext";

// Ventana que usa Compras (o el administrador) para dejar lista la documentación de un pago ANTES de que
// aparezca en Pagos: la solicitud de pago autorizada y los otros documentos.
// Las dos casillas son obligatorias para enviar a Pagos; los números, notas y archivos son opcionales.
function PrepararPagoModal({ titulo, proveedor, monto, datos, guardando, error, onGuardar, onCerrar }) {
  const [form, setForm] = useState({
    solicitud_pago_ok: !!datos?.solicitud_pago_ok,
    solicitud_pago_numero: datos?.solicitud_pago_numero || "",
    solicitud_pago_archivo: datos?.solicitud_pago_archivo || "",
    otros_docs_ok: !!datos?.otros_docs_ok,
    otros_docs_nota: datos?.otros_docs_nota || "",
    otros_docs_archivo: datos?.otros_docs_archivo || "",
  });
  const [subiendo, setSubiendo] = useState("");
  const [errorArchivo, setErrorArchivo] = useState("");
  const refSolicitud = useRef(null);
  const refOtros = useRef(null);

  const cambiar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const listo = form.solicitud_pago_ok && form.otros_docs_ok;

  // Se pueden adjuntar varios archivos por casilla (se agregan a los que ya hay).
  const subir = async (campo, archivos) => {
    const elegidos = [...(archivos || [])];
    if (elegidos.length === 0) return;
    setSubiendo(campo);
    setErrorArchivo("");
    let lista = listaArchivos(form[campo]);
    try {
      for (const archivo of elegidos) {
        const id = await subirArchivo(archivo);
        if (unirArchivos([...lista, id]).length > LARGO_MAXIMO_ARCHIVOS) {
          setErrorArchivo(`No caben más archivos en esta casilla: se adjuntaron ${lista.length}.`);
          break;
        }
        lista = [...lista, id];
        cambiar(campo, unirArchivos(lista));
      }
    } catch (err) {
      setErrorArchivo(err.message);
    } finally {
      setSubiendo("");
    }
  };
  const quitar = (campo, id) => cambiar(campo, unirArchivos(listaArchivos(form[campo]).filter((x) => x !== id)));

  const bloqueDocumento = ({ casilla, etiqueta, ayuda, campoTexto, textoPlaceholder, campoArchivo, refArchivo }) => (
    <div style={{ ...estilos.bloque, ...(form[casilla] ? estilos.bloqueListo : {}) }}>
      <label style={estilos.casilla}>
        <input
          type="checkbox"
          checked={form[casilla]}
          disabled={guardando}
          onChange={(e) => cambiar(casilla, e.target.checked)}
        />
        <span>
          <strong>{etiqueta}</strong>
          <Ayuda>
          <span style={estilos.ayuda}> {ayuda}</span>
          </Ayuda>
        </span>
      </label>
      <div style={estilos.fila}>
        <input
          type="text"
          value={form[campoTexto]}
          onChange={(e) => cambiar(campoTexto, e.target.value)}
          placeholder={textoPlaceholder}
          disabled={guardando}
          style={estilos.input}
        />
        <input
          ref={refArchivo}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }}
          onChange={(e) => {
            subir(campoArchivo, e.target.files);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          disabled={guardando || subiendo === campoArchivo}
          onClick={() => refArchivo.current?.click()}
          style={estilos.botonSecundario}
        >
          📎 {subiendo === campoArchivo ? "Subiendo…" : form[campoArchivo] ? "Agregar archivos" : "Adjuntar (opcional)"}
        </button>
      </div>
      {listaArchivos(form[campoArchivo]).length > 0 && (
        <div style={estilos.listaArchivos}>
          {listaArchivos(form[campoArchivo]).map((id, i) => (
            <span key={id} style={estilos.chipArchivo}>
              <a href="#ver-archivo" onClick={(e) => abrirArchivoSeguro(e, id)} style={estilos.link}>
                📄 Archivo {i + 1}
              </a>
              <button
                type="button"
                onClick={() => quitar(campoArchivo, id)}
                disabled={guardando}
                aria-label={`Quitar archivo ${i + 1}`}
                title="Quitar este archivo"
                style={estilos.quitarArchivo}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={estilos.fondo} onClick={guardando ? undefined : onCerrar}>
      <div style={estilos.caja} onClick={(e) => e.stopPropagation()}>
        <h3 style={estilos.titulo}>📋 {titulo || "Preparar pago"}</h3>
        <p style={estilos.subtitulo}>
          <strong>{proveedor}</strong> · {monto}
        </p>
        <p style={estilos.explicacion}>
          Marca lo que ya tienes listo. Cuando estén las dos casillas, el pago se envía a la pantalla de Pagos.
        </p>

        {bloqueDocumento({
          casilla: "solicitud_pago_ok",
          etiqueta: "Solicitud de pago autorizada",
          ayuda: "— ya la tengo y está autorizada",
          campoTexto: "solicitud_pago_numero",
          textoPlaceholder: "N.º de la solicitud de pago (opcional)",
          campoArchivo: "solicitud_pago_archivo",
          refArchivo: refSolicitud,
        })}
        {bloqueDocumento({
          casilla: "otros_docs_ok",
          etiqueta: "Otros documentos",
          ayuda: "— tengo listos los demás documentos del pago",
          campoTexto: "otros_docs_nota",
          textoPlaceholder: "Qué documentos son (opcional)",
          campoArchivo: "otros_docs_archivo",
          refArchivo: refOtros,
        })}

        {(error || errorArchivo) && <p style={estilos.error}>{error || errorArchivo}</p>}

        <div style={estilos.pie}>
          <button type="button" onClick={onCerrar} disabled={guardando} style={estilos.botonSecundario}>
            Cancelar
          </button>
          <button type="button" onClick={() => onGuardar(form, false)} disabled={guardando} style={estilos.botonSecundario}>
            💾 Guardar borrador
          </button>
          <button
            type="button"
            onClick={() => onGuardar(form, true)}
            disabled={guardando || !listo}
            title={listo ? "" : "Marca las dos casillas para poder enviar a Pagos"}
            style={{ ...estilos.botonPrimario, opacity: listo ? 1 : 0.5 }}
          >
            {guardando ? "Enviando…" : "✓ Enviar a Pagos"}
          </button>
        </div>
      </div>
    </div>
  );
}

const estilos = {
  fondo: { position: "fixed", inset: 0, background: "rgba(27,42,74,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: "1rem" },
  caja: { background: "#fff", borderRadius: 16, padding: "1.4rem 1.6rem", width: "100%", maxWidth: 680, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 16px 48px rgba(27,42,74,0.35)" },
  titulo: { margin: 0, color: "var(--tema-titulo, #0b3c60)", fontSize: "1.1rem" },
  subtitulo: { margin: "0.3rem 0 0", color: "var(--tema-titulo, #0b3c60)", fontSize: "0.9rem" },
  explicacion: { margin: "0.5rem 0 0.9rem", color: "#5b6472", fontSize: "0.82rem" },
  bloque: { border: "1px solid var(--tema-linea, #d5deec)", borderRadius: 10, padding: "0.8rem 0.9rem", marginBottom: "0.7rem", background: "#fff" },
  bloqueListo: { background: "#f1f9f4", borderColor: "#bfe3cd" },
  casilla: { display: "flex", gap: "0.55rem", alignItems: "flex-start", cursor: "pointer", fontSize: "0.9rem", color: "var(--tema-titulo, #0b3c60)" },
  ayuda: { color: "#5b6472", fontWeight: 400, fontSize: "0.8rem" },
  fila: { display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.6rem" },
  input: { flex: 1, minWidth: 200, border: "1px solid #d3d9df", borderRadius: 8, padding: "0.4rem 0.6rem", fontSize: "0.85rem", fontFamily: "inherit" },
  link: { color: "var(--tema-primario, #2f6690)", fontWeight: 600, fontSize: "0.82rem" },
  listaArchivos: { display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.55rem" },
  chipArchivo: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    background: "#fff",
    border: "1px solid var(--tema-linea, #d5deec)",
    borderRadius: 999,
    padding: "0.15rem 0.3rem 0.15rem 0.65rem",
  },
  quitarArchivo: {
    border: "none",
    background: "#eef1f3",
    color: "#5b6472",
    borderRadius: "50%",
    width: 20,
    height: 20,
    fontSize: "0.7rem",
    cursor: "pointer",
    lineHeight: 1,
  },
  error: { color: "#a3402f", fontSize: "0.85rem", margin: "0.3rem 0 0" },
  pie: { display: "flex", justifyContent: "flex-end", gap: "0.5rem", flexWrap: "wrap", marginTop: "1rem" },
  botonSecundario: { border: "1px solid #d3d9df", background: "#fff", color: "var(--tema-titulo, #0b3c60)", borderRadius: 8, padding: "0.5rem 0.9rem", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer" },
  botonPrimario: { border: "none", background: "#2f7a54", color: "#fff", borderRadius: 8, padding: "0.5rem 1.1rem", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" },
};

export default PrepararPagoModal;
